import { useState, useEffect, useRef, useCallback, FormEvent } from 'react';
import { ShieldCheck, MessageSquare, Mail } from 'lucide-react';
import { MfaChallenge } from '../../contexts/AuthContext';
import { ButtonWithIcon } from '../ui/button';
import { OtpInput } from './OtpInput';
import { useResendTimer } from '../../hooks/useResendTimer';

interface MfaVerifyStepProps {
  challenge: MfaChallenge;
  rememberMe: boolean;
  onVerify: (mfaToken: string, otpCode: string, rememberMe: boolean) => Promise<void>;
  onResend?: () => Promise<void>;
  error?: string;
}

export default function MfaVerifyStep({ challenge, rememberMe, onVerify, onResend, error }: MfaVerifyStepProps) {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(challenge.otpExpirySeconds);
  const [localError, setLocalError] = useState('');
  const resendTimer = useResendTimer(30);
  const autoSubmitRef = useRef(false);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const formatCountdown = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handleSubmit = useCallback(async (e?: FormEvent) => {
    e?.preventDefault();
    if (otp.length !== 6 || loading) return;
    setLocalError('');
    setLoading(true);
    try {
      await onVerify(challenge.mfaToken, otp, rememberMe);
    } finally {
      setLoading(false);
    }
  }, [otp, loading, challenge.mfaToken, rememberMe, onVerify]);

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (otp.length === 6 && !loading && !autoSubmitRef.current) {
      autoSubmitRef.current = true;
      handleSubmit();
    }
    if (otp.length < 6) {
      autoSubmitRef.current = false;
    }
  }, [otp, loading, handleSubmit]);

  const handleResend = async () => {
    if (!onResend || resendTimer.isActive) return;
    setResending(true);
    setOtp('');
    setLocalError('');
    try {
      await onResend();
      setSecondsLeft(challenge.otpExpirySeconds);
      resendTimer.start();
    } finally {
      setResending(false);
    }
  };

  const displayError = error || localError;
  const isExpired = secondsLeft <= 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Icon & header */}
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-soft dark:bg-primary/10">
          <ShieldCheck className="w-6 h-6 text-primary" />
        </div>
        <h3 className="text-base font-semibold text-foreground">Verify your identity</h3>
        <p className="text-sm text-muted-foreground">
          A verification code was sent via{' '}
          {challenge.mfaChannel === 'SMS'
            ? <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> SMS</span>
            : <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> email</span>
          }{' '}
          to <span className="font-medium text-foreground">{challenge.maskedContact}</span>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {/* OTP input */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-foreground">
            Verification code
          </label>
          <OtpInput
            value={otp}
            onChange={setOtp}
            error={!!displayError}
            disabled={loading || isExpired}
          />
          {/* Countdown */}
          <p className={`text-xs text-right ${isExpired ? 'text-destructive' : 'text-muted-foreground'}`}>
            {isExpired ? 'Code expired' : `Expires in ${formatCountdown(secondsLeft)}`}
          </p>
        </div>

        {displayError && (
          <p className="text-sm text-destructive text-center" role="alert">{displayError}</p>
        )}

        <ButtonWithIcon
          type="submit"
          icon={ShieldCheck}
          disabled={loading || otp.length !== 6 || isExpired}
          className="w-full"
        >
          {loading ? 'Verifying...' : 'Verify code'}
        </ButtonWithIcon>
      </form>

      {/* Standard bottom actions */}
      <div className="text-center space-y-2">
        {onResend && (
          <p className="text-sm text-muted-foreground">
            Didn't receive code?{' '}
            <button
              type="button"
              onClick={handleResend}
              disabled={resending || resendTimer.isActive}
              className="text-primary hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resendTimer.isActive ? `Resend in ${resendTimer.seconds}s` : resending ? 'Sending...' : 'Resend'}
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
